import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner, Table } from 'react-bootstrap';
import { rmApi } from '../../services/api';
import { FaArrowLeft, FaSpinner, FaExclamationTriangle, FaCheck, FaFilter, FaSearch } from 'react-icons/fa';
import Select from 'react-select';

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

function CreatePayout() {
  const navigate = useNavigate();
  const [dsoOptions, setDsoOptions] = useState([]);
  const [selectedDso, setSelectedDso] = useState(null);
  const [selectedCases, setSelectedCases] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [eligibleCases, setEligibleCases] = useState([]);
  const [loading, setLoading] = useState({
    dsos: true,
    cases: false,
    submit: false
  });
  const [error, setError] = useState({
    dsos: '',
    cases: '',
    submit: ''
  });
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [casePage, setCasePage] = useState(0);
  const [caseSearchTerm, setCaseSearchTerm] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const casesPerPage = 10;

  // Fetch DSOs when component mounts
  useEffect(() => {
    const fetchDsos = async () => {
      setLoading(prev => ({ ...prev, dsos: true }));
      setError(prev => ({ ...prev, dsos: '' }));

      try {
        const response = await rmApi.getAssignedDsos({ limit: 100 }); // Fetch all DSOs
        const dsos = response.data.items || [];
        
        // Format for react-select
        const options = dsos.map(dso => ({
          value: dso.id,
          label: `${dso.name} (${dso.email || dso.phone})`
        }));
        setDsoOptions(options);
      } catch (err) {
        console.error("Error fetching DSOs:", err);
        setError(prev => ({ ...prev, dsos: 'Failed to load DSOs. Please refresh and try again.' }));
      } finally {
        setLoading(prev => ({ ...prev, dsos: false }));
      }
    };

    fetchDsos();
  }, []);

  // Fetch eligible cases when DSO is selected
  useEffect(() => {
    const fetchEligibleCases = async () => {
      if (!selectedDso) return;

      setLoading(prev => ({ ...prev, cases: true }));
      setError(prev => ({ ...prev, cases: '' }));
      setSelectedCases([]);
      setSelectAll(false);

      try {
        // Call the endpoint for all DSO's cases with status=disbursed
        const response = await rmApi.getDsoCases(selectedDso.value, {
          skip: casePage * casesPerPage,
          limit: casesPerPage,
          search: caseSearchTerm,
          status_filter: "disbursed" // Only disbursed cases are eligible
        });
        
        // The backend now automatically filters out cases that are already in approved/disbursed payouts
        // So we can use all returned cases as eligible
        const eligibleCasesData = response.data.items;
        setEligibleCases(eligibleCasesData);
      } catch (err) {
        console.error("Error fetching eligible cases:", err);
        setError(prev => ({ ...prev, cases: 'Failed to load eligible cases. Please try again.' }));
      } finally {
        setLoading(prev => ({ ...prev, cases: false }));
      }
    };

    fetchEligibleCases();
  }, [selectedDso, casePage, caseSearchTerm]);

  // Calculate total amount from selected cases
  useEffect(() => {
    const total = selectedCases.reduce((acc, curr) => {
      // Use payout_amount field if it exists, otherwise fallback to 0
      const amount = curr.payout_dso_amount ? curr.payout_dso_amount : 0;
      return acc + amount;
    }, 0);
    setTotalAmount(total);
  }, [selectedCases]);

  // Handle DSO selection change
  const handleDsoChange = (selected) => {
    setSelectedDso(selected);
    setCasePage(0);
    setCaseSearchTerm('');
  };

  // Handle case selection
  const handleCaseSelection = (caseItem) => {
    setSelectedCases(prev => {
      const caseIndex = prev.findIndex(c => c.id === caseItem.id);
      if (caseIndex >= 0) {
        // Remove case if already selected
        return prev.filter(c => c.id !== caseItem.id);
      } else {
        // Add case if not selected
        return [...prev, caseItem];
      }
    });
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCases([]);
    } else {
      setSelectedCases(eligibleCases);
    }
    setSelectAll(!selectAll);
  };

  // Handle search form submit
  const handleSearch = (e) => {
    e.preventDefault();
    setCasePage(0); // Reset to first page when searching
  };

  // Handle create payout submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedCases.length === 0) {
      setError(prev => ({ ...prev, submit: 'Please select at least one case.' }));
      return;
    }

    setLoading(prev => ({ ...prev, submit: true }));
    setError(prev => ({ ...prev, submit: '' }));
    setSubmitSuccess('');

    try {
      // Extract case IDs from selected cases array
      const caseIds = selectedCases.map(caseItem => caseItem.id);
      
      // Prepare payload for API
      const payoutData = {
        dso_id: selectedDso.value,
        case_ids: caseIds
      };
      
      // Call the API to create the payout
      const response = await rmApi.createPayout(payoutData);
      
      // Success message
      setSubmitSuccess('Payout created successfully!');
      
      // Redirect to payouts list after a brief delay
      setTimeout(() => {
        navigate('/rm/payouts');
      }, 2000);
    } catch (err) {
      console.error("Error creating payout:", err);
      const errorMessage = err.response?.data?.detail || 'Failed to create payout. Please try again.';
      
      // Check if the error is related to commission not being set up
      if (errorMessage.includes('Commission not set up')) {
        setError(prev => ({ 
          ...prev, 
          submit: (
            <div>
              <strong>Commission Error:</strong> {errorMessage}
              <div className="mt-2">
                <small className="text-muted">
                  Please ask the DSO to set up commissions for all products before creating a payout.
                </small>
              </div>
            </div>
          )
        }));
      } else {
        setError(prev => ({ ...prev, submit: errorMessage }));
      }
    } finally {
      setLoading(prev => ({ ...prev, submit: false }));
    }
  };

  // Function to render status badge
  const renderStatusBadge = (status) => {
    if (!status) return null;
    
    let badgeClass = '';
    let statusText = status;
    
    if (typeof status === 'string') {
      statusText = status;
      status = status.toLowerCase();
    }

    switch (status) {
      case 'disbursed':
      case 13:
        badgeClass = 'rm-status-approved';
        statusText = 'Disbursed';
        break;
      default:
        badgeClass = 'rm-status-unknown';
    }
    
    return <span className={`rm-status-badge ${badgeClass}`}>{statusText}</span>;
  };

  return (
    <div className="rm-create-payout-page">
      <div className="rm-page-header">
        <div className="rm-header-left">
          <Link to="/rm/payouts" className="rm-back-link">
            <FaArrowLeft /> Back to Payouts
          </Link>
          <h2>Create New Payout</h2>
        </div>
      </div>

      {/* Main form */}
      <Card className="rm-card">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            {/* Step 1: Select DSO */}
            <div className="rm-form-section">
              <h4 className="rm-section-title">Step 1: Select DSO</h4>
              <Form.Group className="mb-4">
                <Form.Label>DSO</Form.Label>
                <div className="dso-select-container">
                  {loading.dsos ? (
                    <div className="rm-loading-inline">
                      <FaSpinner className="rm-spinner" />
                      <span>Loading DSOs...</span>
                    </div>
                  ) : error.dsos ? (
                    <Alert variant="danger" className="p-2 mb-0">
                      <FaExclamationTriangle className="me-2" />
                      {error.dsos}
                    </Alert>
                  ) : (
                    <Select
                      options={dsoOptions}
                      value={selectedDso}
                      onChange={handleDsoChange}
                      isSearchable
                      placeholder="Select a DSO..."
                      className="rm-react-select-container"
                      classNamePrefix="rm-react-select"
                      isDisabled={loading.submit}
                    />
                  )}
                </div>
              </Form.Group>
            </div>

            {/* Step 2: Select Cases */}
            {selectedDso && (
              <div className="rm-form-section">
                <h4 className="rm-section-title">Step 2: Select Cases</h4>

                {/* Search form */}
                <div className="rm-cases-filter mb-3">
                  <Form onSubmit={handleSearch}>
                    <div className="rm-search-row">
                      <Form.Group className="rm-search-input">
                        <Form.Control
                          type="text"
                          placeholder="Search by lead name or application number"
                          value={caseSearchTerm}
                          onChange={(e) => setCaseSearchTerm(e.target.value)}
                          disabled={loading.cases || loading.submit}
                        />
                      </Form.Group>
                      <Button 
                        type="submit"
                        variant="primary"
                        className="rm-search-btn"
                        disabled={loading.cases || loading.submit}
                      >
                        <FaSearch className="me-1" /> Search
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={() => setCaseSearchTerm('')}
                        disabled={!caseSearchTerm || loading.cases || loading.submit}
                        className="rm-reset-btn"
                      >
                        Reset
                      </Button>
                    </div>
                  </Form>
                </div>

                {/* Cases table */}
                {loading.cases ? (
                  <div className="rm-loading-state">
                    <FaSpinner className="rm-spinner" />
                    <span>Loading eligible cases...</span>
                  </div>
                ) : error.cases ? (
                  <Alert variant="danger">
                    <FaExclamationTriangle className="me-2" />
                    {error.cases}
                  </Alert>
                ) : eligibleCases.length > 0 ? (
                  <div className="rm-cases-table-container">
                    <Table striped bordered hover responsive className="rm-cases-table">
                      <thead>
                        <tr>
                          <th>
                            <Form.Check
                              type="checkbox"
                              checked={selectAll}
                              onChange={handleSelectAll}
                              disabled={loading.submit}
                              title="Select all cases"
                            />
                          </th>
                          <th>Lead Name</th>
                          <th>Application No.</th>
                          <th>Product</th>
                          <th>Status</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibleCases.map(caseItem => (
                          <tr 
                            key={caseItem.id} 
                            className={selectedCases.some(c => c.id === caseItem.id) ? "selected-row" : ""}
                          >
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={selectedCases.some(c => c.id === caseItem.id)}
                                onChange={() => handleCaseSelection(caseItem)}
                                disabled={loading.submit}
                              />
                            </td>
                            <td>{caseItem.lead_name}</td>
                            <td>{caseItem.application_no}</td>
                            <td>{caseItem.product_name}</td>
                            <td>{renderStatusBadge(caseItem.case_status_str || caseItem.case_status)}</td>
                            <td>{formatCurrency(caseItem.payout_dso_amount)}</td> {/* Example amount */}
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {/* Pagination controls */}
                    <div className="rm-pagination-controls">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setCasePage(prev => Math.max(0, prev - 1))}
                        disabled={casePage === 0 || loading.cases || loading.submit}
                      >
                        Previous Page
                      </Button>
                      <span className="rm-page-info">Page {casePage + 1}</span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setCasePage(prev => prev + 1)}
                        disabled={eligibleCases.length < casesPerPage || loading.cases || loading.submit}
                      >
                        Next Page
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Alert variant="info">No eligible cases found for this DSO.</Alert>
                )}

                {/* Selection summary */}
                {selectedCases.length > 0 && (
                  <div className="rm-selection-summary">
                    <div className="rm-summary-item">
                      <span className="rm-summary-label">Cases Selected:</span>
                      <span className="rm-summary-value">{selectedCases.length}</span>
                    </div>
                    <div className="rm-summary-item">
                      <span className="rm-summary-label">Total Amount:</span>
                      <span className="rm-summary-value">{formatCurrency(totalAmount)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Submit */}
            {selectedDso && (
              <div className="rm-form-section">
                <h4 className="rm-section-title">Step 3: Create Payout</h4>
                
                {error.submit && (
                  <Alert variant="danger" className="mb-3">
                    <FaExclamationTriangle className="me-2" />
                    {error.submit}
                  </Alert>
                )}
                
                {submitSuccess && (
                  <Alert variant="success" className="mb-3">
                    <FaCheck className="me-2" />
                    {submitSuccess}
                  </Alert>
                )}

                <div className="rm-submit-section">
                  <Button
                    type="submit"
                    variant="primary"
                    className="rm-submit-btn"
                    disabled={selectedCases.length === 0 || loading.submit}
                  >
                    {loading.submit ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating Payout...
                      </>
                    ) : (
                      'Create Payout'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Form>
        </Card.Body>
      </Card>

      <style jsx>{`
        .rm-create-payout-page {
          padding: 20px;
          background-color: #f8fafc;
        }
        
        .rm-page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        
        .rm-header-left {
          display: flex;
          flex-direction: column;
        }
        
        .rm-back-link {
          display: inline-flex;
          align-items: center;
          color: #64748b;
          text-decoration: none;
          font-size: 0.875rem;
          margin-bottom: 8px;
        }
        
        .rm-back-link:hover {
          color: #334155;
        }
        
        .rm-page-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #0f172a;
        }
        
        .rm-card {
          background-color: #ffffff;
          border: none;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 20px;
        }
        
        .rm-form-section {
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .rm-form-section:last-child {
          margin-bottom: 0;
          padding-bottom: 0;
          border-bottom: none;
        }
        
        .rm-section-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #334155;
          margin-bottom: 16px;
        }
        
        .dso-select-container {
          min-height: 38px;
        }
        
        .rm-loading-inline {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.875rem;
          color: #64748b;
        }
        
        .rm-spinner {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .rm-cases-filter {
          background-color: #f1f5f9;
          padding: 16px;
          border-radius: 6px;
        }
        
        .rm-search-row {
          display: flex;
          gap: 8px;
        }
        
        .rm-search-input {
          flex: 1;
        }
        
        .rm-cases-table-container {
          margin-top: 16px;
          overflow-x: auto;
        }
        
        .rm-cases-table th,
        .rm-cases-table td {
          vertical-align: middle;
          font-size: 0.875rem;
        }
        
        .rm-cases-table th:first-child,
        .rm-cases-table td:first-child {
          width: 40px;
          text-align: center;
        }
        
        .selected-row {
          background-color: rgba(59, 130, 246, 0.05) !important;
        }
        
        .rm-pagination-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 16px;
          padding: 8px 0;
        }
        
        .rm-page-info {
          font-size: 0.875rem;
          color: #64748b;
        }
        
        .rm-selection-summary {
          display: flex;
          flex-wrap: wrap;
          gap: 24px;
          margin-top: 20px;
          padding: 16px;
          background-color: #f8fafc;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
        }
        
        .rm-summary-item {
          display: flex;
          flex-direction: column;
        }
        
        .rm-summary-label {
          font-size: 0.75rem;
          color: #64748b;
          margin-bottom: 4px;
        }
        
        .rm-summary-value {
          font-size: 1.125rem;
          font-weight: 600;
          color: #0f172a;
        }
        
        .rm-submit-section {
          display: flex;
          justify-content: flex-end;
          margin-top: 20px;
        }
        
        .rm-submit-btn {
          min-width: 150px;
        }
        
        .rm-loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          color: #64748b;
        }
        
        /* Status Badges */
        .rm-status-badge {
          display: inline-block;
          padding: 4px 8px;
          font-size: 0.7rem;
          font-weight: 600;
          line-height: 1;
          color: #fff;
          text-align: center;
          white-space: nowrap;
          vertical-align: baseline;
          border-radius: 0.25rem;
          text-transform: capitalize;
        }
        
        .rm-status-approved { background-color: #10b981; }
        .rm-status-unknown { background-color: #94a3b8; }
        
        /* React Select Styles */
        .rm-react-select-container {
          min-width: 250px;
        }
        
        .rm-react-select__control {
          min-height: 38px;
          border-color: #cbd5e1;
          box-shadow: none;
        }
        
        .rm-react-select__control--is-focused {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 1px #3b82f6 !important;
        }
        
        .rm-react-select__menu {
          z-index: 10;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .rm-search-row {
            flex-direction: column;
          }
          
          .rm-search-btn,
          .rm-reset-btn {
            width: 100%;
            margin-top: 8px;
          }
          
          .rm-selection-summary {
            flex-direction: column;
            gap: 12px;
          }
        }
      `}</style>
    </div>
  );
}

export default CreatePayout;